import Box from "@mui/material/Box";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef, useState } from "react";

import type { SetupEventView, TimeCheckResult } from "@/libs/tournamentSetupApi";

import { TermHelp } from "../setup/term-help";
import { Mono, Pill, STAFF_FONT, tone } from "../setup/ui";

export type GroupBy = "day" | "discipline" | "division" | "none";
export type ColumnSet = "full" | "panel" | "bulk";
const VIRTUALIZE_AFTER = 60;

type Row =
  | { type: "group"; key: string; title: string; ids: string[]; collapsed: boolean }
  | { type: "event"; key: string; event: SetupEventView };

function groupTitle(by: GroupBy, label: string, events: SetupEventView[], selected: Set<string>, result: TimeCheckResult): string {
  const count = `${events.length} event${events.length === 1 ? "" : "s"}`;
  const all = events.length > 1 && events.every((event) => selected.has(event.event_id)) ? " · both selected" : "";
  const allLabel = events.length > 2 && all ? " · all selected" : all;
  if (by === "day") {
    const day = result.days.find((d) => events[0].stage_days.some((s) => s.play_date === d.play_date));
    return `${label} · ${count}${day ? ` · ${day.used_court_hours} of ${day.capacity_court_hours} court-hours` : ""}`;
  }
  return `${label} · ${count}${allLabel}`;
}

/** Events grouped by day, discipline or division; groups collapse to a count,
 * shift-click selects a range, long lists virtualize (F2, F7). */
export function EventsTable({
  events,
  groupBy,
  selected,
  onSelect,
  opened = null,
  onOpen,
  columns,
  result,
}: {
  events: SetupEventView[];
  groupBy: GroupBy;
  /** Ticked rows (bulk edit). */
  selected: Set<string>;
  onSelect: (ids: Set<string>) => void;
  /** The row shown in the panel; opening a row never ticks it. */
  opened?: string | null;
  onOpen?: (id: string) => void;
  columns: ColumnSet;
  result: TimeCheckResult;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const anchor = useRef<string | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);

  // Server order within groups; groups in first-appearance order, days by date.
  const ordered = useMemo(() => {
    if (groupBy !== "day") return events;
    return [...events].sort((a, b) => a.stage_days[0].play_date.localeCompare(b.stage_days[0].play_date));
  }, [events, groupBy]);

  const rows: Row[] = useMemo(() => {
    if (groupBy === "none") return ordered.map((event) => ({ type: "event", key: event.event_id, event }));
    const groups = new Map<string, SetupEventView[]>();
    for (const event of ordered) {
      const label = event.group_labels[groupBy];
      groups.set(label, [...(groups.get(label) ?? []), event]);
    }
    const out: Row[] = [];
    for (const [label, members] of groups) {
      const isCollapsed = collapsed.has(label);
      out.push({ type: "group", key: `g:${label}`, title: groupTitle(groupBy, label, members, selected, result), ids: members.map((m) => m.event_id), collapsed: isCollapsed });
      if (!isCollapsed) out.push(...members.map((event) => ({ type: "event" as const, key: event.event_id, event })));
    }
    return out;
  }, [ordered, groupBy, collapsed, selected, result]);

  const visibleIds = ordered.map((event) => event.event_id);
  const virtual = rows.length > VIRTUALIZE_AFTER;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: (index) => (rows[index].type === "group" ? 24 : 30),
    overscan: 12,
    enabled: virtual,
  });

  const clickRow = (id: string, event: React.MouseEvent | React.KeyboardEvent, viaCheckbox: boolean) => {
    if (!viaCheckbox && !("shiftKey" in event && event.shiftKey)) {
      onOpen?.(id);
      return;
    }
    if ("shiftKey" in event && event.shiftKey && anchor.current) {
      const a = visibleIds.indexOf(anchor.current);
      const b = visibleIds.indexOf(id);
      const [from, to] = a < b ? [a, b] : [b, a];
      onSelect(new Set([...selected, ...visibleIds.slice(from, to + 1)]));
      return;
    }
    anchor.current = id;
    if (viaCheckbox) {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelect(next);
    }
  };

  const showDay = columns === "bulk" || groupBy !== "day";
  const showDraws = columns !== "bulk";
  const showPartner = columns !== "panel";
  const headers = [
    "Event",
    ...(showDay ? ["Day"] : []),
    "Entry",
    "Format",
    ...(showDraws ? ["Draws", "Guaranteed"] : []),
    "Fee / entry",
    "Limit",
    ...(showPartner ? ["Partner"] : []),
  ];
  const colCount = headers.length + 1;

  const renderRow = (row: Row) => {
    if (row.type === "group") {
      const allOn = row.ids.every((id) => selected.has(id));
      return (
        <Box component="tr" key={row.key} sx={{ "& td": { height: 24, bgcolor: tone.page, fontSize: 12, fontWeight: 700, color: tone.muted, borderBottom: `1px solid ${tone.divider}` } }}>
          <Box component="td" sx={{ pl: "14px", width: 22 }}>
            <input
              aria-label={`Select all ${row.title.split(" · ")[0]}`}
              checked={allOn}
              onChange={() => onSelect(allOn ? new Set([...selected].filter((id) => !row.ids.includes(id))) : new Set([...selected, ...row.ids]))}
              style={{ width: 15, height: 15, accentColor: tone.brand, margin: 0 }}
              type="checkbox"
            />
          </Box>
          <Box component="td" colSpan={colCount - 1}>
            <Box
              aria-expanded={!row.collapsed}
              component="button"
              onClick={() =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  const label = row.key.slice(2);
                  if (next.has(label)) next.delete(label);
                  else next.add(label);
                  return next;
                })
              }
              sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "inherit", cursor: "pointer" }}
              type="button"
            >
              {row.title}
              {row.collapsed ? " ▸" : ""}
            </Box>
          </Box>
        </Box>
      );
    }
    const event = row.event;
    const ticked = selected.has(event.event_id);
    const on = ticked || opened === event.event_id;
    const amber = event.format_label === "Format set when registration closes";
    return (
      <Box
        aria-selected={on}
        data-opened={opened === event.event_id || undefined}
        component="tr"
        key={row.key}
        onClick={(e: React.MouseEvent) => clickRow(event.event_id, e, false)}
        sx={{
          cursor: "pointer",
          "& td": { height: 29, px: "8px", borderBottom: `1px solid ${tone.divider}`, color: tone.ink2, whiteSpace: "nowrap", bgcolor: on ? tone.brandNote : "transparent" },
          "& td:first-of-type": { boxShadow: on ? "inset 3px 0 0 ${tone.brand}" : "none" },
          "&:hover td": { bgcolor: on ? tone.brandNote : tone.page },
        }}
      >
        <Box component="td" onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ pl: "14px !important", width: 22 }}>
          <input
            aria-label={`Select ${event.name}`}
            checked={ticked}
            onChange={() => undefined}
            onClick={(e: React.MouseEvent) => clickRow(event.event_id, e, true)}
            style={{ width: 15, height: 15, accentColor: tone.brand, margin: 0 }}
            type="checkbox"
          />
        </Box>
        <Box component="td" sx={{ fontWeight: 600, color: `${tone.ink} !important` }}>
          <Box
            component="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onOpen?.(event.event_id);
            }}
            sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left" }}
            type="button"
          >
            {event.name}
          </Box>
        </Box>
        {showDay ? <td>{event.day_labels.join(" → ")}</td> : null}
        <td>{event.entry_unit === "players" ? "Players" : "Pairs"}</td>
        <td>
          <Pill variant={amber ? "amber" : "neutral"}>{event.format_label}</Pill>
        </td>
        {showDraws ? <td>{event.draws_label}</td> : null}
        {showDraws ? (
          <td>
            <Mono>{event.guarantee}</Mono>
          </td>
        ) : null}
        <td>
          <Mono>{event.fee_label}</Mono>
        </td>
        <td>
          {event.entry_limit} {event.entry_unit}
          {event.entries_in ? (
            <Box component="span" sx={{ color: tone.subtle }}>
              {" "}
              · {event.entries_in} in
            </Box>
          ) : null}
        </td>
        {showPartner ? <td>{event.partner_label}</td> : null}
      </Box>
    );
  };

  const items = virtual ? virtualizer.getVirtualItems() : null;
  const padTop = items && items.length ? items[0].start : 0;
  const padBottom = items && items.length ? virtualizer.getTotalSize() - items[items.length - 1].end : 0;

  return (
    <Box ref={scroller} sx={{ overflow: "auto", flexGrow: 1, minHeight: 0, maxHeight: virtual ? 640 : "none" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, fontFamily: STAFF_FONT }}>
        <thead>
          <Box component="tr" sx={{ "& th": { height: 28, textAlign: "left", fontSize: 11.5, fontWeight: 600, color: tone.subtle, px: "8px", borderBottom: `1px solid ${tone.line}`, whiteSpace: "nowrap", position: "sticky", top: 0, bgcolor: tone.surface, zIndex: 1 } }}>
            <Box component="th" sx={{ width: 22, pl: "14px !important" }}>
              <input
                aria-label="Select all events"
                checked={events.length > 0 && events.every((event) => selected.has(event.event_id))}
                onChange={(e) => onSelect(e.target.checked ? new Set(visibleIds) : new Set())}
                style={{ width: 15, height: 15, accentColor: tone.brand, margin: 0 }}
                type="checkbox"
              />
            </Box>
            {headers.map((header) => (
              <th key={header}>
                {header === "Guaranteed" ? (
                  <>
                    Guaranteed <TermHelp label="What does guaranteed mean?" term="guaranteed_matches" />
                  </>
                ) : (
                  header
                )}
              </th>
            ))}
          </Box>
        </thead>
        <tbody>
          {virtual && items ? (
            <>
              {padTop ? (
                <tr aria-hidden style={{ height: padTop }}>
                  <td colSpan={colCount} />
                </tr>
              ) : null}
              {items.map((item) => renderRow(rows[item.index]))}
              {padBottom ? (
                <tr aria-hidden style={{ height: padBottom }}>
                  <td colSpan={colCount} />
                </tr>
              ) : null}
            </>
          ) : (
            rows.map(renderRow)
          )}
        </tbody>
      </Box>
    </Box>
  );
}
